import { useEffect, useState } from 'react';
import { Layout, Menu, Drawer, Typography, Button, Avatar, Space } from 'antd';
import type { MenuProps } from 'antd';
import {
	AppstoreOutlined,
	SettingOutlined,
	DollarCircleOutlined,
	BranchesOutlined,
	ApiOutlined,
	TeamOutlined,
	MessageOutlined,
	SoundOutlined,
	RiseOutlined,
	PartitionOutlined,
	MenuOutlined,
	BarChartOutlined,
	CustomerServiceOutlined,
	MobileOutlined,
	MailOutlined,
	CommentOutlined,
	PicCenterOutlined,
	WalletOutlined,
	InboxOutlined,
} from '@ant-design/icons';
import { ENoviDashboardSider } from '@/pages/Novi/enums/ENoviDashboardSider';
import { getResellerData } from '@/store/reseller/selector';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getCampaignToken } from '@/api/novi/campaigns';
import LocalStorageUtility from '@/utils/localStorage';
import { LocalStorageKeys } from 'src/enums/ELocalStorage';
import { useDispatch } from 'react-redux';
// import { setCampaignPreview, setNoviSelectedCampaign, setNoviSelectedCampaignId } from '@/store/novi/user/slice';
import { User, UserAccount } from '@/types/TAccounts';
import { getLoggedInUser, getOutboundAgents } from '@/store/account/selector';
import { setAgentPreview, setSelectedAgent, setSelectedAgentId } from '@/store/account/slice';
import { AppRoutes } from 'src/enums/ERoutes';
import { useNavigate } from 'react-router-dom';

const { Sider } = Layout;
const { Title } = Typography;

interface SiderMenuProps {
	currentPage: ENoviDashboardSider | string;
	handleMenuSelect: (key: ENoviDashboardSider) => void;
	selectedCampaignId: string | null;
	noviUser?: UserAccount;
	isMobile: boolean;
	showBusinessInfo?: boolean;
	menuRef: React.RefObject<HTMLDivElement>;
	brandingRef?: React.MutableRefObject<HTMLElement | null>;
	byotRef?: React.MutableRefObject<HTMLElement | null>;
	campaignsRef: React.MutableRefObject<HTMLElement | null>;
	isStandaloneCampaign?: boolean;
	resellerLogoUrl?: string;
	collapsible?: boolean;
	resellerView?: boolean;
	campaigns?: any[];
}

const SiderMenu: React.FC<SiderMenuProps> = ({
	currentPage,
	handleMenuSelect,
	selectedCampaignId,
	isMobile,
	showBusinessInfo = true,
	menuRef,
	isStandaloneCampaign = false,
	resellerLogoUrl,
	resellerView = false,
	collapsible = false,
	campaigns = [],
}) => {
	const dispatch = useDispatch();
	const outboundAgents = useAppSelector(getOutboundAgents);
	const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
	const [collapsed, setCollapsed] = useState<boolean>(false);
	const currentReseller = useAppSelector(getResellerData);
	const loggedInUser = useAppSelector(getLoggedInUser);
	const navigate = useNavigate();

	useEffect(() => {
		setCollapsed(false); // Keep sidebar expanded by default
	}, []);

	const handleDrawerClose = () => setIsDrawerOpen(false);

	const getToken = async (campaign_id: string) => {
		try {
			const clientType = resellerView ? 'Reseller' : 'Client';
			const response = await getCampaignToken(clientType, campaign_id);
			LocalStorageUtility.setLocalData(LocalStorageKeys.AUTH_TOKEN, response.auth_token);
		} catch (error) {
			console.error('Error fetching token:', error);
		}
	};

	const handleOpenCampaign = async (number: string) => {
		await getToken(number);
		dispatch(setSelectedAgentId({ selectedAgentId: number }));
		dispatch(
			setSelectedAgent({ user: outboundAgents.find((agent) => agent.number === number) as User }),
		);
		dispatch(setAgentPreview({ agentPreview: true }));
	};

	const handleOpenAgent = (number: string) => {
		dispatch(setSelectedAgentId({ selectedAgentId: number }));
		dispatch(
			setSelectedAgent({ user: outboundAgents.find((agent) => agent.number === number) as User }),
		);
		navigate(AppRoutes.OUTBOUND_AGENT_PORTAL.replace(':agentId', number));
	};

	const menuItems: MenuProps['items'] = selectedCampaignId
		? [
				{
					type: 'submenu',
					label: 'My Campaigns',
					key: 'my-campaigns',
					children: outboundAgents.map((agent) => ({
						key: agent.number,
						label: agent.name,
						onClick: () => handleOpenAgent(agent.number),
						icon: <PicCenterOutlined />,
						className: `${selectedCampaignId === agent.number ? 'bg-[#e6f4ff]' : ''}`,
					})),
				},
				{
					type: 'group',
					label: 'CAMPAIGN MANAGEMENT',
					children: [
						{
							key: ENoviDashboardSider.CAMPAIGN_DASHBOARD,
							icon: <BarChartOutlined />,
							label: 'Dashboard',
						},
						{
							key: ENoviDashboardSider.CAMPAIGN_SETTINGS,
							icon: <RiseOutlined />,
							label: 'Campaign Settings',
						},
						{
							key: ENoviDashboardSider.RESERVE_NUMBER,
							icon: <MobileOutlined />,
							label: 'Reserve Number',
						},
						{
							key: ENoviDashboardSider.CRM,
							icon: <CustomerServiceOutlined />,
							label: 'AI CRM',
						},
						{
							key: ENoviDashboardSider.LEADS,
							icon: <TeamOutlined />,
							label: 'Leads Manager',
						},
						{
							key: ENoviDashboardSider.VARIABLES,
							icon: <PartitionOutlined />,
							label: 'Variables',
						},
						{
							key: ENoviDashboardSider.ALL_LOGS,
							icon: <InboxOutlined />,
							label: 'Inbox',
						},

						// {
						// 	key: ENoviDashboardSider.WEBHOOKS,
						// 	icon: <ApiOutlined />,
						// 	label: 'Webhooks',
						// },

						// !resellerView
						// 	? {
						// 			key: ENoviDashboardSider.NOTIFICATIONS,
						// 			icon: <NotificationAddSharp />,
						// 			label: 'Notifications',
						// 		}
						// 	: null,
					],
				},
				{
					type: 'group',
					label: 'COMMUNICATION CHANNELS',
					children: [
						{
							key: 'phone_agent',
							icon: <MobileOutlined />,
							label: 'Phone Agent',
							children: [
								{
									key: ENoviDashboardSider.CONFIGURE,
									icon: <SettingOutlined />,
									label: 'Configuration',
								},
								// { key: ENoviDashboardSider.SCHEDULE, icon: <SettingOutlined />, label: 'Configuration' },
								{
									key: ENoviDashboardSider.VOICE_LIBRARY,
									icon: <SoundOutlined />,
									label: 'Voice Library',
								},
							],
						},
						// {
						// 	key: 'text_agent',
						// 	icon: <MessageOutlined />,
						// 	label: 'Texting Agent',
						// 	children: [
						// 		{
						// 			key: ENoviDashboardSider.TEXT_CONFIGURATION,
						// 			icon: <CommentOutlined />,
						// 			label: 'Configuration',
						// 		},
						// 		{
						// 			key: ENoviDashboardSider.TEXTING_AGENT,
						// 			icon: <MessageOutlined />,
						// 			label: 'Texting Platform',
						// 		},
						// 	],
						// },
					],
				},
				// {
				//   type: 'group',
				//   label: 'AI & Automation',
				//   children: [

				//     {
				//       key: ENoviDashboardSider.INTAKE_FORMS,
				//       icon: <FileTextOutlined />,
				//       label: 'Intake Forms',
				//     },

				//     {
				//       key: ENoviDashboardSider.WEBHOOKS,
				//       icon: <ApiOutlined />,
				//       label: 'Webhooks',
				//     },

				//   ],
				// },
				// {
				//   type: 'group',
				//   label: 'ADVANCED SETTINGS',
				//   children: [
				//     {
				//       key: ENoviDashboardSider.CONFIGURE,
				//       icon: <RobotOutlined />,
				//       label: 'AI Settings',
				//     },
				//   ],
				// },
				// {
				//   type: 'group',
				//   label: 'Logs & Analytics',
				//   children: [
				//     {
				//       key: ENoviDashboardSider.ANALYTICS,
				//       icon: <BarChartOutlined />,
				//       label: 'Analytics',
				//     },
				//   ],
				// },
			]
		: [
				{
					key: ENoviDashboardSider.DASHBOARD,
					icon: <AppstoreOutlined />,
					label: 'Dashboard',
				},
				{
					key: ENoviDashboardSider.MY_CAMPAIGNS,
					icon: <AppstoreOutlined />,
					label: 'My Campaigns',
					children: campaigns.map((campaign) => ({
						key: campaign.number,
						label: campaign.name,
						onClick: () => handleOpenCampaign(campaign.number),
					})),
				},
				{
					key: ENoviDashboardSider.MANAGE_CREDITS,
					icon: <WalletOutlined />,
					label: 'Manage Credits',
				},
				{
					key: 'settings',
					icon: <SettingOutlined />,
					label: 'Settings',
					children: [
						{ key: ENoviDashboardSider.BRANDING, icon: <BranchesOutlined />, label: 'Branding' },
						{ key: ENoviDashboardSider.BYOT, icon: <ApiOutlined />, label: 'Bring Your Twilio' },
						{
							key: ENoviDashboardSider.SUBSCRIPTION,
							icon: <DollarCircleOutlined />,
							label: 'Subscription',
						},
						{
							key: ENoviDashboardSider.EMAIL,
							icon: <MailOutlined />,
							label: 'Email Configuration',
						},
					],
				},
			];

	const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
		if (outboundAgents.find((agent) => agent.number === key)) {
			return;
		}

		handleMenuSelect(key as ENoviDashboardSider);
		if (isMobile) {
			handleDrawerClose();
		}
	};

	const MenuHeader = ({ collapsed }: { collapsed: boolean }) => (
		<div className="p-4 border-b border-gray-100">
			<Space direction="vertical" size="small" style={{ width: '100%' }}>
				<Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
					<Avatar
						src={isStandaloneCampaign && resellerLogoUrl ? resellerLogoUrl : undefined}
						size={48}
						style={{
							padding: 4,
							backgroundColor: '#f5f5f5',
							boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
						}}
					/>
				</Space>

				{!collapsed && (
					<Title level={5} style={{ margin: 0 }}>
						{isStandaloneCampaign && currentReseller?.businessName
							? currentReseller.businessName
							: loggedInUser
								? loggedInUser.businessName
								: 'Welcome Back'}
					</Title>
				)}
			</Space>
		</div>
	);

	const sidebarContent = (
		<>
			{showBusinessInfo && <MenuHeader collapsed={collapsed} />}
			<div ref={menuRef} className="px-3">
				<Menu
					mode="inline"
					selectedKeys={[currentPage]}
					onClick={handleMenuClick}
					items={menuItems}
					theme="light"
				/>
			</div>
		</>
	);

	return isMobile ? (
		<>
			<Button
				type="text"
				icon={<MenuOutlined />}
				onClick={() => setIsDrawerOpen(true)}
				style={{
					position: 'fixed',
					top: '16px',
					left: '16px',
					zIndex: 1000,
				}}
			/>
			<Drawer
				title={null}
				placement="left"
				onClose={handleDrawerClose}
				open={isDrawerOpen}
				width={280}
				styles={{
					body: { padding: 0, backgroundColor: '#fff' },
					mask: { backgroundColor: 'rgba(0, 0, 0, 0.2)' },
				}}
				closable={false}
			>
				{sidebarContent}
			</Drawer>
		</>
	) : (
		<Sider
			theme="light"
			width={250}
			collapsible={collapsible}
			collapsed={collapsed}
			onCollapse={setCollapsed}
			className="common-sider"
		>
			<div style={{ overflowX: 'hidden' }}>{sidebarContent}</div>
		</Sider>
	);
};

export default SiderMenu;
